import classNames from 'classnames';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Else, If, Then, When } from 'react-if';

import {
  Button,
  Clickable,
  IconCheckmarkCircle,
  IconCross,
  IconError,
  IconInfo,
  IconWarning,
} from '../';
import { ToasterType, ToastParams, TranslationKeys } from '../types';

export const Toaster: React.FC<
  {
    message?: TranslationKeys;
    type?: ToasterType;
    className?: React.ComponentProps<'div'>['className'];
    closeToast?: () => void;
  } & Partial<Exclude<ToastParams, TranslationKeys>>
> = ({
  message,
  type,
  className,
  closeToast,
  titleKey,
  textKey,
  buttonLabel,
  onButtonClick,
  showCloseBtn,
  onClose,
  onClick,
  text,
}) => {
  const { t } = useTranslation();

  const renderIcon = () => {
    switch (type) {
      case 'info':
        return <IconInfo />;
      case 'error':
        return <IconError />;
      case 'success':
        return <IconCheckmarkCircle />;
      case 'warning':
        return <IconWarning />;
      default:
        return null;
    }
  };

  const toasterContainer = classNames('toaster', className, {
    [`toaster-${type}`]: type,
  });

  return (
    <Clickable
      className={toasterContainer}
      onClick={() => onClick && onClick()}
    >
      <div className="toaster-icon-container">{renderIcon()}</div>
      <If condition={message}>
        <Then>
          <div className="toaster-message-container-text">
            <Trans t={t}>{message}</Trans>
          </div>
        </Then>
        <Else>
          <div className="toaster-content">
            <div className="toaster-message-container">
              <div>
                <div className="toaster-message-container-title">
                  <Trans t={t}>{titleKey}</Trans>
                </div>
                <div className="toaster-message-container-text">
                  <If condition={text}>
                    <Then>
                      <span>{text}</span>
                    </Then>
                    <Else>
                      <Trans t={t}>{textKey}</Trans>
                    </Else>
                  </If>
                </div>
              </div>
            </div>
            {buttonLabel && onButtonClick && (
              <div className="toaster-btn">
                <Button
                  variant="text"
                  isLink={true}
                  linkType="primary-light"
                  textKey={buttonLabel}
                  onClick={() => {
                    onButtonClick();
                    if (closeToast) closeToast();
                  }}
                />
              </div>
            )}
          </div>
        </Else>
      </If>
      <When condition={showCloseBtn}>
        <div className="toaster-close-btn">
          <Button
            variant="text"
            isLink={true}
            linkType="primary-light"
            Icon={IconCross}
            onClick={() => {
              if (onClose) onClose();
              if (closeToast) closeToast();
            }}
          />
        </div>
      </When>
    </Clickable>
  );
};
